import * as types from '../actions/actionTypes';

/**
 * @typedef {import('../containers/Pages/TagManager/types').TagRow} TagRow
 * @typedef {import('../containers/Pages/TagManager/types').TagManagerState} TagManagerState
 */

/** @type {TagManagerState} */
export const initialState = {
  tags: [],
  allTags: [],
  enabled: null,
  tagsStatus: 'idle',
  allTagsStatus: 'idle',
  configStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  tagsError: null,
  allTagsError: null,
  configError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  lastCreatedTag: null,
  lastUpdatedTag: null,
};

/**
 * Reducer for the Tag Manager slice. Keeps the fetched active tags, all tags,
 * the client feature flag, and the mutation request states together with
 * their independent request states so a failure on one endpoint never
 * discards useful data from the other.
 *
 * @param {TagManagerState} state - The current tag manager state.
 * @param {Object} action - The dispatched action.
 * @returns {TagManagerState} The new tag manager state.
 */
export default function tagManager(state = initialState, action = {}) {
  switch (action.type) {
    case types.TAG_MANAGER_TAGS_REQUEST:
      return {
        ...state,
        tagsStatus: 'loading',
        tagsError: null,
      };
    case types.TAG_MANAGER_TAGS_REQUEST_SUCCESS:
      return {
        ...state,
        tagsStatus: 'loaded',
        tags: action.result,
      };
    case types.TAG_MANAGER_TAGS_REQUEST_FAILED:
      return {
        ...state,
        tagsStatus: 'failed',
        tagsError: action.error,
      };
    case types.TAG_MANAGER_ALL_TAGS_REQUEST:
      return {
        ...state,
        allTagsStatus: 'loading',
        allTagsError: null,
      };
    case types.TAG_MANAGER_ALL_TAGS_REQUEST_SUCCESS:
      return {
        ...state,
        allTagsStatus: 'loaded',
        allTags: action.result,
      };
    case types.TAG_MANAGER_ALL_TAGS_REQUEST_FAILED:
      return {
        ...state,
        allTagsStatus: 'failed',
        allTagsError: action.error,
      };
    case types.TAG_MANAGER_CONFIG_SET_REQUEST:
    case types.TAG_MANAGER_CONFIG_REQUEST:
      return {
        ...state,
        configStatus: 'loading',
        configError: null,
      };
    case types.TAG_MANAGER_CONFIG_REQUEST_SUCCESS:
    case types.TAG_MANAGER_CONFIG_SET_REQUEST_SUCCESS:
      return {
        ...state,
        configStatus: 'loaded',
        enabled: action.result,
      };
    case types.TAG_MANAGER_CONFIG_REQUEST_FAILED:
    case types.TAG_MANAGER_CONFIG_SET_REQUEST_FAILED:
      return {
        ...state,
        configStatus: 'failed',
        configError: action.error,
      };
    case types.TAG_MANAGER_TAG_CREATE_REQUEST:
      return {
        ...state,
        createStatus: 'loading',
        createError: null,
      };
    case types.TAG_MANAGER_TAG_CREATE_REQUEST_SUCCESS:
      return {
        ...state,
        createStatus: 'loaded',
        lastCreatedTag: action.result,
      };
    case types.TAG_MANAGER_TAG_CREATE_REQUEST_FAILED:
      return {
        ...state,
        createStatus: 'failed',
        createError: action.error,
      };
    case types.TAG_MANAGER_TAG_UPDATE_REQUEST:
      return {
        ...state,
        updateStatus: 'loading',
        updateError: null,
      };
    case types.TAG_MANAGER_TAG_UPDATE_REQUEST_SUCCESS:
      return {
        ...state,
        updateStatus: 'loaded',
        lastUpdatedTag: action.result,
      };
    case types.TAG_MANAGER_TAG_UPDATE_REQUEST_FAILED:
      return {
        ...state,
        updateStatus: 'failed',
        updateError: action.error,
      };
    case types.TAG_MANAGER_TAG_DELETE_REQUEST:
      return {
        ...state,
        deleteStatus: 'loading',
        deleteError: null,
      };
    case types.TAG_MANAGER_TAG_DELETE_REQUEST_SUCCESS:
      return {
        ...state,
        deleteStatus: 'loaded',
      };
    case types.TAG_MANAGER_TAG_DELETE_REQUEST_FAILED:
      return {
        ...state,
        deleteStatus: 'failed',
        deleteError: action.error,
      };

    default:
      return state;
  }
}
